import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FILTER_CONTENT_ITEM,
  ISelectInput,
  SearchSelector,
  SearchOperator,
} from '@portal/shared/ui/filter';
import { FilterStaticBase } from '@portal/shared/ui/filter/src/lib/components/static-base';
import { TransactionFilterList } from '../transaction-filters.list';
import { TransactionCurrencies } from './currency.list';
import { FiltersService } from '@portal/shared/ui/filter/src/lib/services/filters.service';
import { SupportedProcessorSettingsService } from '@apps/portal/src/app/services/supported-processor-settings.service';
import { TransactionFilter } from '../../../enums/transaction-filter.enum';
import cloneDeep from 'lodash/cloneDeep';
import { Subscription } from 'rxjs';
import { Directions } from '@portal/shared/core/src/lib/enums/direction.enum';

@Component({
  selector: 'portal-transaction-filter-currency',
  templateUrl: './currency.component.html',
  providers: [
    {
      provide: FILTER_CONTENT_ITEM,
      useExisting: FilterTransactionCurrencyComponent,
    },
  ],
  standalone: false,
})
export class FilterTransactionCurrencyComponent extends FilterStaticBase {
  @Input() isMainDropdownFilter: boolean;
  @Input() showFilterSubMenu: boolean;
  @Input() isHeightAuto = false;
  @Output()
  currencyTextName: EventEmitter<string> = new EventEmitter();

  currencyText = this.transactionfilters.list.find(
    (filter) => filter.id === TransactionFilter.Currency,
  ).text;
  readonly Directions = Directions;

  constructor(
    private currencies: TransactionCurrencies,
    private transactionfilters: TransactionFilterList,
    private filtersService: FiltersService,
    private supportedProcessorSettingService: SupportedProcessorSettingsService,
  ) {
    super(SearchSelector.Currency, currencies.getSupportedCurrencies());
    this.intermediatePreSelectedEntities = new Map();
    this.addButtonLabel = $localize`Add`;
    this.filtersService.actionRemoveFilter$.subscribe((entityToRemove: ISelectInput) => {
      if (SearchSelector.Currency === entityToRemove.key) {
        const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
        if (selectedEntity?.id) {
          this.onEntityRemoved(entityToRemove);
          this.onFilterApplied();
          this.itemToggled.emit();
        }
      }
    });

    this.supportedProcessorSettingService.isProcessorFetched$.subscribe(() => {
      this.setAllEntities(currencies.getSupportedCurrencies());
    });

    this.subMenuComponentSelected$.subscribe((submenu) => {
      if (SearchSelector.Currency.includes(submenu)) {
        this.synchronizeLocalPreSelectedEntities();
      }
      this.updateEntities();
    });

    this.updateEntities$.subscribe(() => {
      if (this.isMainDropdownFilter) {
        this.synchronizeLocalPreSelectedEntities();
      }
      this.updateEntities();
    });

    this.updateCounter$.subscribe(() => {
      const localPreSelected: Map<string, ISelectInput> = new Map();
      if (this.criteriaMapPreSelected.has(SearchSelector.Currency)) {
        const argumentArr = this.getArgumentArray(
          this.criteriaMapPreSelected,
          SearchSelector.Currency,
        );

        if (this.criteriaMapPreSelected.get(SearchSelector.Currency)?.argument) {
          argumentArr?.forEach((argument) => {
            localPreSelected.set(argument, {
              id: argument,
              text: this.currencies.list.find((currency) => currency.id === argument)?.text || '',
            });
          });
          this.localPreSelectedEntities = cloneDeep(localPreSelected);
        } else {
          this.localPreSelectedEntities.clear();
        }
      }
      this.updateEntities();
    });
  }

  addIntermediateEntity(selectedEntity: ISelectInput): void {
    this.intermediatePreSelectedEntities.set(selectedEntity.id, selectedEntity);
    this.setAddButtonLabel();
  }

  onEntityRemoved(selectedEntity: ISelectInput): void {
    this.intermediatePreSelectedEntities.delete(selectedEntity.id);
    this.setAddButtonLabel();
  }

  onFilterAddedAndEntitiesUpdated(): void {
    this.entities = this.currencies.list;
    this.localPreSelectedEntities.clear();
    this.intermediatePreSelectedEntities.forEach((entity) => {
      this.localPreSelectedEntities.set(entity.id, entity);
    });
    this.onFilterAdded();
    this.updateEntities();
    if (this.isOpen) {
      this.closeSubmenus$.next(true);
    }
    this.currencyTextName.emit(this.currencyText);
    this.isOpen = false;
  }

  onFilterAdded(): void {
    this.updateEntities();
    const values: string[] = this.getValues();
    if (values.length) {
      this.filterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        operator: SearchOperator.In,
        value: Array.from(this.localPreSelectedEntities.values()),
        argument: `(${Array.from(values).join(',')})`,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        value: Array.from(this.localPreSelectedEntities.values()),
      });
    } else {
      this.filterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
    }

    this.searchedText = '';
    this.itemToggled.emit();
    this.updateSwitchToggleState();
  }

  protected override subscribeToRemoveEntityObservable(): Subscription {
    return this.entityRemoved$.subscribe((entityToRemove: ISelectInput) => {
      const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
      if (selectedEntity) {
        this.onEntityRemoved(entityToRemove);
        this.onFilterAdded();
        this.isOpen = false;
      }
    });
  }

  private synchronizeLocalPreSelectedEntities(): void {
    if (this.criteriaMapPreSelected?.get(SearchSelector.Currency)?.argument) {
      const argumentArr = this.getArgumentArray(this.selectedEntities, SearchSelector.Currency);

      this.localPreSelectedEntities.clear();
      this.intermediatePreSelectedEntities.clear();
      argumentArr?.forEach((argument) => {
        this.setLocalPreSelectedEntities(
          argument,
          this.currencies.list.find((currency) => currency.id === argument)?.text || '',
        );
      });
      this.localPreSelectedEntities.forEach((entity) => {
        this.intermediatePreSelectedEntities.set(entity.id, entity);
      });
    } else {
      this.localPreSelectedEntities.clear();
      this.intermediatePreSelectedEntities.clear();
    }
    this.updateEntities();
    this.setAddButtonLabel();
  }

  onClear(): void {
    this.clearStaticMultiSelect();
    this.entities = this.currencies.getSupportedCurrencies();
    this.removeStaticFilter();
    this.currencyTextName.emit(this.currencyText);
  }
}
